/**
 * useRoyaleTournaments / useRoyaleTournamentDetail
 *
 * GET /fun_challenges/v1/royale_tournaments/?state=scheduled,live
 * GET /fun_challenges/v1/royale_tournaments/<id>/
 *
 * Reads for the staff Host panel (all scheduled/live tournaments) and the
 * tournaments-page discovery + completed history view. Lifecycle writes
 * (create/start/cancel/register/withdraw) go through the SOCKET (useRoyaleSocket),
 * not here — this hook is read-only. The durable numeric `id` is the identity;
 * `session_id` links completed results. All datetimes are UTC ISO strings.
 */

import {useQuery} from '@tanstack/react-query';

import ChessRestClient from '@/Api/ChessRestClient';
import {noteServerNow} from '@/Screens/FunChallenges/util/serverClock';

export type RoyaleTournamentState =
  | 'scheduled'
  | 'live'
  | 'completed'
  | 'cancelled';

export interface RoyaleTournamentSummary {
  id: number;
  state: RoyaleTournamentState;
  share_code: string | null;
  session_id: string | null;
  room_id: string | null;
  created_by: number;
  created_by_name: string | null;
  scheduled_at: string | null; // UTC ISO
  started_at: string | null;
  ended_at: string | null;
  max_players: number;
  timer_sec: number;
  num_puzzles: number | null;
  topics: string[];
  // 'tourney' = the ramping 400→2200 rating ladder (≤4 moves), the DEFAULT for
  // scheduled tournaments; easy/medium/hard remain selectable by the host.
  // Server-authoritative (chess `_TOURNAMENT_LADDER`). Spelled short because the
  // column is VARCHAR(8) — see TOURNAMENT_DIFFICULTY in royaleConstants.
  difficulty: 'easy' | 'medium' | 'hard' | 'tourney';
  /** True => serve the host's uploaded .pgn set instead of the difficulty band.
   *  Can be true with an EMPTY set (flag ticked at create, upload never done),
   *  in which case the race falls back to normal band puzzles rather than
   *  failing to start. The authoritative count is the host-only
   *  `.../puzzles/` status endpoint (useTournamentPuzzleStatus). */
  custom_puzzles: boolean;
  /** "Punish mistakes": punish_threshold wrong answers ELIMINATES a player (the
   *  race continues for everyone else). OPTIONAL — an older Django omits both. */
  punish?: boolean;
  punish_threshold?: number | null;
  name: string | null;          // host title; null/blank => "Puzzle Royale #id"
  description: string | null;   // optional Details blurb
  /** Private => an entry password is required to JOIN (enforced in Django's
   *  royale_tournament_register). The tournament is still LISTED in discovery —
   *  privacy gates registration, not visibility. */
  is_private: boolean;
  has_password: boolean;        // whether a password is set (raw pw never sent)
  player_count: number | null;
  /**
   * Whether the REQUESTING user holds a registered roster row. Server-authoritative,
   * so the "joined" tick is correct on every tab.
   *
   * OPTIONAL: an older Django build omits the key entirely. Callers must treat
   * `undefined` as "unknown" and fall back to their previous inference (the
   * `mine=1` id-set intersection) rather than reading it as `false`.
   */
  is_registered?: boolean;
}

export interface RoyaleTournamentRosterEntry {
  user_id: number;
  user_name: string | null;
  status: 'registered' | 'withdrawn';
  registered_at: string | null;
}

export interface RoyaleTournamentPlayerResult {
  user_id: number;
  points: number;
  score: number;
  wrong: number;
  best_combo: number;
  piece: string;
  rank: number;
  accuracy: number;
  solved_ids: number[];
  failed_ids: number[];
  attempts: unknown[];
  eliminated: boolean;
  finished_at: string | null;
}

export interface RoyaleTournamentDetail extends RoyaleTournamentSummary {
  roster: RoyaleTournamentRosterEntry[];
  /**
   * Server wall clock (epoch ms) at response time — used to correct device
   * clock skew before rendering the "Game starts in" countdown. Optional: an
   * older Django build omits it, in which case the offset simply isn't updated.
   */
  server_now?: number;
  results:
    | (Record<string, unknown> & {
        players: RoyaleTournamentPlayerResult[];
        puzzle_royale_detail: Record<string, unknown> | null;
      })
    | null;
}

/** List tournaments by state (host panel + discovery). Default state omitted =>
 *  server returns the scheduled+live set. Poll while a lobby is open so the
 *  roster count + state changes surface without a manual refresh. */
export function useRoyaleTournaments(
  states?: RoyaleTournamentState[],
  opts?: {refetchIntervalMs?: number; enabled?: boolean; mine?: boolean},
) {
  const stateParam = (states && states.length ? states.join(',') : '');
  const mine = !!opts?.mine;
  return useQuery<RoyaleTournamentSummary[], unknown>(
    ['royale_tournaments', stateParam, mine ? 'mine' : 'all'],
    async () => {
      const params: string[] = [];
      if (stateParam) params.push(`state=${encodeURIComponent(stateParam)}`);
      if (mine) {
        params.push('mine=1');
        // The "Joined" list is a history that only grows, and the server caps
        // this endpoint at 50 rows unless asked otherwise (ceiling 100). A
        // player with more registrations than that silently lost the overflow —
        // no error, no "load more", the rows simply weren't in the payload.
        // Ask for the server's maximum. Discovery deliberately doesn't: that
        // set is scheduled+live only and self-prunes, so 50 is plenty.
        params.push('limit=100');
      }
      const qs = params.length ? `?${params.join('&')}` : '';
      const res = await ChessRestClient.get(
        `fun_challenges/v1/royale_tournaments/${qs}`,
      );
      return (res.data as RoyaleTournamentSummary[]) || [];
    },
    {
      enabled: opts?.enabled !== false,
      refetchInterval: opts?.refetchIntervalMs,
      staleTime: 5_000,
    },
  );
}

/** One tournament by durable id: summary + roster + (if completed) results. */
export function useRoyaleTournamentDetail(
  tournamentId: number | string | null | undefined,
  opts?: {refetchIntervalMs?: number; enabled?: boolean},
) {
  const idStr = tournamentId != null ? String(tournamentId) : '';
  return useQuery<RoyaleTournamentDetail, unknown>(
    ['royale_tournament_detail', idStr],
    async () => {
      const res = await ChessRestClient.get(
        `fun_challenges/v1/royale_tournaments/${idStr}/`,
      );
      // Calibrate the device↔server clock skew from Django's `server_now`
      // (epoch ms, additive). This screen renders "Game starts in m:ss" BEFORE
      // any socket envelope arrives, so without this a cold page load on a
      // mis-set device had no offset and mis-rendered the countdown.
      // Deliberately inside `queryFn`: it runs only on a real network response,
      // never on a react-query cache hit (whose timestamp would be stale).
      noteServerNow((res.data as any)?.server_now);
      return res.data as RoyaleTournamentDetail;
    },
    {
      enabled: !!idStr && opts?.enabled !== false,
      refetchInterval: opts?.refetchIntervalMs,
      staleTime: 5_000,
    },
  );
}
